import type { ILeadQueueRepository } from '@/domain/repositories/ILeadQueueRepository';
import type { ISyncBookkeepingRepository } from '@/domain/repositories/ISyncBookkeepingRepository';
import type { IActiveSessionRepository } from '@/domain/repositories/IActiveSessionRepository';
import type { ILeadRepository } from '@/domain/repositories/ILeadRepository';
import type { ActiveSessionSnapshot } from '@/domain/entities/ActiveSessionSnapshot';
import { isActiveSessionExpired, toDeviceSessionFromSnapshot } from '@/domain/entities/ActiveSessionSnapshot';
import type { QueuedLead } from '@/domain/entities/QueuedLead';
import {
  hasCompleteConsentData,
  isPermanentSyncFailure,
  toLeadCaptureInput,
} from '@/domain/entities/QueuedLead';
import { createEmptySyncBookkeeping, recordSyncedLead } from '@/domain/entities/SyncBookkeeping';
import { DeviceSessionExpiredError } from '@/infrastructure/http/DeviceSessionExpiredError';
import {
  ConsentNotApprovedError,
  ConsentVersionNotFoundError,
  LeadEventNotFoundError,
} from '@/infrastructure/repositories/HttpLeadRepository';

/** Tempo máximo de espera por uma tentativa de sincronização de um único
 * lead (Spec Técnica > Considerações de performance) — implementado aqui
 * via `Promise.race` porque `HttpLeadRepository.captureLead` (Fatia 1) é
 * reaproveitado tal como está, sem suporte a `AbortSignal`. */
const SYNC_REQUEST_TIMEOUT_MS = 30_000;

export type SyncLeadQueueSkipReason = 'no-active-session' | 'session-expired' | 'empty-queue';

export interface SyncLeadQueueResult {
  synced: number;
  failed: number;
  skipped: SyncLeadQueueSkipReason | null;
}

export interface SyncLeadQueueDependencies {
  leadQueueRepository: ILeadQueueRepository;
  syncBookkeepingRepository: ISyncBookkeepingRepository;
  activeSessionRepository: IActiveSessionRepository;
  leadRepository: ILeadRepository;
  /** Instante atual — injetável para testes determinísticos. */
  now?: () => Date;
}

class SyncTimeoutError extends Error {
  constructor() {
    super('Tempo limite excedido ao sincronizar lead (30s).');
    this.name = 'SyncTimeoutError';
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new SyncTimeoutError()), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/** Mapeia o erro lançado por `HttpLeadRepository.captureLead` para um HTTP
 * status aproximado — usado só para auditoria local (`QueuedLead.lastError`,
 * útil para QA/operador avançado), nunca para decidir se o lead sai da
 * fila (isso é decidido só pelo try/catch: qualquer exceção mantém o lead
 * na fila para retry, CA-05). */
function extractErrorStatus(error: unknown): number {
  if (error instanceof DeviceSessionExpiredError) return 410;
  if (error instanceof LeadEventNotFoundError) return 404;
  if (error instanceof ConsentNotApprovedError) return 409;
  if (error instanceof ConsentVersionNotFoundError) return 422;
  if (error instanceof SyncTimeoutError) return 0;
  if (error instanceof Error) {
    const match = /HTTP (\d+)/.exec(error.message);
    if (match) return Number(match[1]);
  }
  return 0;
}

/**
 * Sincroniza a `lead_queue` pendente do evento da sessão ativa com o
 * servidor. Módulo **sem nenhuma dependência de DOM/React** — só
 * `fetch`/`indexedDB` (via os repositórios injetados), ambos disponíveis
 * tanto em `window` quanto em `ServiceWorkerGlobalScope` — importável tanto
 * por `src/sw.ts` (evento `sync`) quanto pelo fallback de polling no main
 * thread (CA-05/CA-06), sem duplicar a lógica de sincronização.
 *
 * Processa os leads pendentes sequencialmente (não em paralelo): não há
 * meta de performance formal para esta fatia (Spec Técnica > Considerações
 * de performance) e o processamento sequencial evita saturar o servidor
 * durante uma reconexão com fila grande.
 */
export class SyncLeadQueueUseCase {
  constructor(private readonly deps: SyncLeadQueueDependencies) {}

  async execute(): Promise<SyncLeadQueueResult> {
    const now = this.deps.now ?? ((): Date => new Date());
    const snapshot = await this.deps.activeSessionRepository.get();

    if (!snapshot) {
      return { synced: 0, failed: 0, skipped: 'no-active-session' };
    }

    if (isActiveSessionExpired(snapshot, now())) {
      return { synced: 0, failed: 0, skipped: 'session-expired' };
    }

    const pending = await this.deps.leadQueueRepository.getPendingForEvent(snapshot.eventId);
    if (pending.length === 0) {
      return { synced: 0, failed: 0, skipped: 'empty-queue' };
    }

    let synced = 0;
    let failed = 0;

    for (const lead of pending) {
      const success = await this.syncOne(lead, snapshot, now);
      if (success) synced += 1;
      else failed += 1;
    }

    return { synced, failed, skipped: null };
  }

  private async syncOne(
    lead: QueuedLead,
    snapshot: ActiveSessionSnapshot,
    now: () => Date
  ): Promise<boolean> {
    // CA-02: registro corrompido/incompleto — POST cancelado, permanece na
    // fila marcado com erro local, log estruturado (mesmo destino do
    // CA-03, Error level, sem exposição em UI).
    if (!hasCompleteConsentData(lead)) {
      console.error(
        '[SyncLeadQueueUseCase] Lead sem consentAccepted/consentTextVersion completos — POST cancelado (CA-02).',
        { leadId: lead.id }
      );
      await this.deps.leadQueueRepository.recordRetryFailure(
        lead.id,
        { status: 0, message: 'Registro sem dados de consentimento completos.' },
        now().toISOString()
      );
      return false;
    }

    try {
      const capturedLead = await withTimeout(
        this.deps.leadRepository.captureLead(
          toDeviceSessionFromSnapshot(snapshot),
          toLeadCaptureInput(lead),
          lead.consentTextVersion
        ),
        SYNC_REQUEST_TIMEOUT_MS
      );

      // HTTP 201 (criado), 200 (idempotente — CA-11) e 202 (consentimento
      // recusado, nenhum lead criado — `captureLead` retorna `null`)
      // chegam aqui sem lançar: todos são tratados como sucesso de
      // sincronização — o lead sai da fila local (CA-05).
      await this.deps.leadQueueRepository.remove(lead.id);
      await this.recordBookkeeping(snapshot, capturedLead?.id ?? lead.id, now());
      return true;
    } catch (error) {
      // 4xx/5xx (via `mapErrorResponse` de `HttpLeadRepository`) ou timeout
      // local: o lead permanece na fila para nova tentativa em ambos os
      // casos (CA-05, sem regressão no comportamento offline-first) — a
      // classificação abaixo (LOCAL-002) só distingue o log e alimenta o
      // estado 'stuck' do indicador (`useLeadQueueSync`), lido a partir de
      // `QueuedLead.lastError.status` persistido por `recordRetryFailure`.
      const status = extractErrorStatus(error);
      const message = error instanceof Error ? error.message : String(error);

      if (isPermanentSyncFailure(status)) {
        console.error(
          '[SyncLeadQueueUseCase] Falha permanente de sincronização (gate de consentimento/versão) — não se resolve sozinha com retry (LOCAL-002).',
          { leadId: lead.id, status }
        );
      }

      await this.deps.leadQueueRepository.recordRetryFailure(
        lead.id,
        { status, message },
        now().toISOString()
      );
      return false;
    }
  }

  private async recordBookkeeping(
    snapshot: ActiveSessionSnapshot,
    serverLeadId: string,
    now: Date
  ): Promise<void> {
    const existing = await this.deps.syncBookkeepingRepository.get(snapshot.deviceId);
    const current = existing ?? createEmptySyncBookkeeping(snapshot.deviceId, snapshot.eventId);
    const updated = recordSyncedLead(current, serverLeadId, now.toISOString());
    await this.deps.syncBookkeepingRepository.save(updated);
  }
}
